import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Modal,
  Row,
  Col,
  Button,
  Input,
  List,
  Tooltip,
  Select as AntSelect,
  Popconfirm,
  Upload,
  message,
} from 'antd';
import {
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  getConfiguracionesDeReportesExportables,
  postConfiguracionDeReporteExportable,
  deleteConfiguracionDeReporteExportable,
} from 'api/configuracionesDeReportesExportables';
import currencyFormatter from 'utils/currencyFormatter';

const { Option } = AntSelect;

const capitalizar = (key) => key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

// formatReceived() (utils/formatValues.js) convierte cualquier campo con forma de fecha
// en una instancia de Moment antes de que los datos lleguen aquí — hay que reconocerla
// como fecha igual que un string crudo 'YYYY-MM-DD' (algunas pantallas, como
// CalculoDeNominas.jsx, usan formatReceivedClone en su lugar y sí entregan strings).
const pareceFecha = (valor) => moment.isMoment(valor)
  || (typeof valor === 'string' && moment(valor, 'YYYY-MM-DD', true).isValid());

// Mitigación estándar de "Formula/CSV Injection": si un texto (título de columna
// importado de una plantilla, o un valor de dato) empieza con un caracter que Excel
// podría interpretar como el inicio de una fórmula, se antepone un apóstrofo para
// forzar que se lea como texto literal. No afecta valores numéricos/booleanos/fecha.
const CARACTERES_DE_FORMULA = ['=', '+', '-', '@', '\t', '\r'];
const sanitizarCeldaExcel = (valor) => {
  if (typeof valor !== 'string' || valor.length === 0) return valor;
  return CARACTERES_DE_FORMULA.includes(valor.charAt(0)) ? `'${valor}` : valor;
};

/**
 * Modal reutilizable para exportar a Excel un arreglo de objetos, eligiendo
 * qué columnas incluir, en qué orden, con qué título y qué formato
 * (Texto/Moneda/Fecha), y guardando/reutilizando esa selección como una
 * configuración con nombre por usuario.
 */
const ExportadorExcelPersonalizado = ({
  visible,
  onCancel,
  data,
  fileName,
  sheetName,
  reporteId,
  // Títulos legibles para llaves cuyo nombre técnico no se reconoce al buscar
  // (p. ej. { importe_imss_sbc: 'Salario Base de Cotización (SBC)' }).
  titulosPorColumna,
  // Formato inicial por llave (p. ej. { importe_neto: 'moneda' }); sin él se infiere del dato.
  formatosPorColumna,
  // Llaves preseleccionadas, en ese orden, al abrir (sin él se preseleccionan todas).
  columnasPorDefecto,
  // Opcional: exporta agrupado por una columna, con renglón de grupo, subtotal por grupo y
  // TOTAL GENERAL de las columnas con formato Moneda (que se escriben como número, sumables).
  // { columna: 'llave', etiqueta: 'Departamento', encabezado: ['línea 1', 'línea 2'] }
  agrupacion,
}) => {
  const { pathname } = useLocation();
  const reporteKey = reporteId ? `${pathname}:${reporteId}` : pathname;

  const [availableColumns, setAvailableColumns] = useState([]);
  const [orderedSelectedColumns, setOrderedSelectedColumns] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [configuracionesGuardadas, setConfiguracionesGuardadas] = useState([]);
  const [nombreNuevaConfiguracion, setNombreNuevaConfiguracion] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedKeys = useMemo(
    () => orderedSelectedColumns.map((col) => col.key),
    [orderedSelectedColumns],
  );

  useEffect(() => {
    if (!visible) return undefined;

    const allKeysSet = new Set();
    (data || []).forEach((record) => {
      Object.keys(record).forEach((key) => allKeysSet.add(key));
    });
    const allKeys = Array.from(allKeysSet).sort();
    const primerRegistro = data?.[0] || {};
    const cols = allKeys.map((key) => {
      let formato = 'texto';
      if (formatosPorColumna?.[key]) {
        formato = formatosPorColumna[key];
      } else if (pareceFecha(primerRegistro[key])) {
        formato = 'fecha';
      } else if (typeof primerRegistro[key] === 'boolean') {
        formato = 'booleano';
      }
      return {
        key,
        dataIndex: key,
        titleText: titulosPorColumna?.[key] || capitalizar(key),
        formato,
      };
    });
    setAvailableColumns(cols);
    if (columnasPorDefecto?.length) {
      const porKey = new Map(cols.map((col) => [col.key, col]));
      setOrderedSelectedColumns(
        columnasPorDefecto.filter((key) => porKey.has(key)).map((key) => porKey.get(key)),
      );
    } else {
      setOrderedSelectedColumns(cols);
    }

    let mounted = true;
    (async () => {
      const configuraciones = await getConfiguracionesDeReportesExportables(reporteKey);
      if (mounted) setConfiguracionesGuardadas(configuraciones || []);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSelectColumn = (col) => {
    setOrderedSelectedColumns([...orderedSelectedColumns, col]);
  };

  const handleRemoveColumn = (col) => {
    setOrderedSelectedColumns(orderedSelectedColumns.filter((c) => c.key !== col.key));
  };

  const handleSelectAll = () => setOrderedSelectedColumns([...availableColumns]);

  const handleDeselectAll = () => setOrderedSelectedColumns([]);

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrdered = [...orderedSelectedColumns];
    [newOrdered[index - 1], newOrdered[index]] = [newOrdered[index], newOrdered[index - 1]];
    setOrderedSelectedColumns(newOrdered);
  };

  const handleMoveDown = (index) => {
    if (index === orderedSelectedColumns.length - 1) return;
    const newOrdered = [...orderedSelectedColumns];
    [newOrdered[index], newOrdered[index + 1]] = [newOrdered[index + 1], newOrdered[index]];
    setOrderedSelectedColumns(newOrdered);
  };

  const handleChangeTitulo = (col, nuevoTitulo) => {
    setOrderedSelectedColumns(orderedSelectedColumns.map(
      (c) => (c.key === col.key ? { ...c, titleText: nuevoTitulo } : c),
    ));
  };

  const handleChangeFormato = (col, nuevoFormato) => {
    setOrderedSelectedColumns(orderedSelectedColumns.map(
      (c) => (c.key === col.key ? { ...c, formato: nuevoFormato } : c),
    ));
  };

  const filteredAvailableColumns = availableColumns.filter((col) => !selectedKeys.includes(col.key)
    && (col.titleText.toLowerCase().includes(searchText.toLowerCase())
      || col.dataIndex.toLowerCase().includes(searchText.toLowerCase())));

  const handleCargarConfiguracion = (id) => {
    const configuracion = configuracionesGuardadas.find((c) => c.id === id);
    if (!configuracion) return;
    setOrderedSelectedColumns(configuracion.columnas);
  };

  const handleGuardarConfiguracion = async () => {
    if (!nombreNuevaConfiguracion.trim()) {
      message.warning('Escribe un nombre para la configuración');
      return;
    }
    const nueva = await postConfiguracionDeReporteExportable({
      reporte_key: reporteKey,
      nombre: nombreNuevaConfiguracion.trim(),
      columnas: orderedSelectedColumns,
    });
    if (nueva) {
      setConfiguracionesGuardadas([...configuracionesGuardadas, nueva]);
      setNombreNuevaConfiguracion('');
    }
  };

  const handleEliminarConfiguracion = async (id) => {
    const eliminada = await deleteConfiguracionDeReporteExportable(id);
    if (eliminada) {
      setConfiguracionesGuardadas(configuracionesGuardadas.filter((c) => c.id !== id));
    }
  };

  // Descarga la selección actual como un archivo .json — para pasarle la misma
  // configuración a otro usuario (de la misma pantalla), sin depender de que ambos
  // estén en la misma entidad/base de datos.
  const handleDescargarPlantilla = () => {
    if (orderedSelectedColumns.length === 0) {
      message.warning('Selecciona al menos una columna para poder descargar la plantilla');
      return;
    }
    const contenido = {
      reporteKey,
      columnas: orderedSelectedColumns.map((col) => ({
        key: col.key,
        titleText: col.titleText,
        formato: col.formato,
      })),
    };
    const blob = new Blob([JSON.stringify(contenido, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plantilla-exportador${reporteId ? `-${reporteId}` : ''}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Lee un archivo .json descargado con handleDescargarPlantilla (propio o de otro
  // usuario) y aplica esa selección de columnas. Solo se quedan las columnas que de
  // verdad existen en los datos actuales — si el archivo es de otra pantalla, avisa.
  const handleImportarPlantilla = (file) => {
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const contenido = JSON.parse(e.target.result);
        const columnasImportadas = Array.isArray(contenido) ? contenido : contenido?.columnas;
        if (!Array.isArray(columnasImportadas)) {
          throw new Error('Formato inválido');
        }
        const porKey = new Map(availableColumns.map((col) => [col.key, col]));
        const encontradas = [];
        const noEncontradas = [];
        columnasImportadas.forEach((col) => {
          if (porKey.has(col.key)) {
            encontradas.push({
              ...porKey.get(col.key), titleText: col.titleText, formato: col.formato,
            });
          } else {
            noEncontradas.push(col.titleText || col.key);
          }
        });
        if (encontradas.length === 0) {
          message.error('Ninguna columna de esta plantilla existe en los datos de esta pantalla');
          return;
        }
        setOrderedSelectedColumns(encontradas);
        if (noEncontradas.length > 0) {
          message.warning(`Se aplicó la plantilla, pero ${noEncontradas.length} columna(s) no existen aquí y se omitieron: ${noEncontradas.join(', ')}`);
        } else {
          message.success('Plantilla aplicada correctamente');
        }
      } catch (err) {
        message.error('El archivo no es una plantilla válida');
      }
    };
    lector.readAsText(file);
    return false;
  };

  // Valor de una celda según el formato de su columna. Un objeto Moment o un booleano crudo
  // nunca deben escribirse tal cual (formatReceived() ya convirtió/dejó así el valor antes de
  // llegar aquí) — se formatean sin importar qué "formato" haya quedado guardado para la columna.
  const valorDeCelda = (col, valor) => {
    if (moment.isMoment(valor)) return valor.isValid() ? valor.format('DD/MM/YYYY') : '';
    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    if (col.formato === 'moneda') return currencyFormatter(parseFloat(valor) || 0);
    if (col.formato === 'fecha' && valor) return moment(valor).format('DD/MM/YYYY');
    return sanitizarCeldaExcel(valor ?? '');
  };

  // Exportación agrupada (prop `agrupacion`): mismo formato que los reportes por Departamento
  // de nómina. Las columnas Moneda se escriben como número con formato de moneda para que se
  // puedan sumar y se totalizan por grupo y en general.
  const exportarAgrupado = () => {
    const { columna, etiqueta = 'Grupo', encabezado = [] } = agrupacion;
    const FORMATO_MONEDA_EXCEL = '"$"#,##0.00';
    const esMoneda = (col) => col.formato === 'moneda';
    const numero = (valor) => {
      const n = parseFloat(valor);
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
    };
    const filaDeRegistro = (record) => orderedSelectedColumns.map(
      (col) => (esMoneda(col) ? numero(record[col.key]) : valorDeCelda(col, record[col.key])),
    );
    const filaDeTotales = (titulo, registros) => {
      const fila = orderedSelectedColumns.map((col) => (esMoneda(col)
        ? Math.round(registros.reduce((acc, r) => acc + numero(r[col.key]), 0) * 100) / 100
        : ''));
      fila[0] = sanitizarCeldaExcel(titulo);
      return fila;
    };

    const filas = encabezado.map((linea) => [sanitizarCeldaExcel(linea)]);
    if (filas.length) filas.push([]);
    filas.push(orderedSelectedColumns.map((col) => sanitizarCeldaExcel(col.titleText)));
    const filaTitulos = filas.length - 1;

    const grupos = new Map();
    (data || []).forEach((record) => {
      const grupo = String(record[columna] ?? '').trim() || 'Sin asignar';
      if (!grupos.has(grupo)) grupos.set(grupo, []);
      grupos.get(grupo).push(record);
    });
    [...grupos.keys()].sort((a, b) => a.localeCompare(b)).forEach((grupo) => {
      const registros = grupos.get(grupo);
      filas.push([sanitizarCeldaExcel(`${etiqueta}: ${grupo}`)]);
      registros.forEach((record) => filas.push(filaDeRegistro(record)));
      filas.push(filaDeTotales(`Total ${etiqueta}: ${grupo}`, registros));
      filas.push([]);
    });
    filas.push(filaDeTotales('TOTAL GENERAL', data || []));

    const worksheet = XLSX.utils.aoa_to_sheet(filas);
    const indicesMoneda = orderedSelectedColumns
      .map((col, idx) => (esMoneda(col) ? idx : -1)).filter((idx) => idx >= 0);
    const rango = XLSX.utils.decode_range(worksheet['!ref']);
    for (let r = filaTitulos + 1; r <= rango.e.r; r += 1) {
      indicesMoneda.forEach((c) => {
        const celda = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (celda && celda.t === 'n') celda.z = FORMATO_MONEDA_EXCEL;
      });
    }
    worksheet['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: filaTitulos + 1 }];
    worksheet['!cols'] = orderedSelectedColumns.map((col) => ({
      wch: Math.max(String(col.titleText).length, 14) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    message.success(`Exportados ${(data || []).length} registros en ${grupos.size} grupos con ${orderedSelectedColumns.length} columnas`);
  };

  const handleExportar = () => {
    if (orderedSelectedColumns.length === 0) {
      message.warning('Selecciona al menos una columna para exportar');
      return;
    }
    setLoading(true);
    if (agrupacion?.columna) {
      try {
        exportarAgrupado();
        onCancel();
      } catch (err) {
        message.error('Error al generar el Excel');
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const excelData = (data || []).map((record) => {
        const row = {};
        orderedSelectedColumns.forEach((col) => {
          // El encabezado puede venir de una plantilla importada (ver
          // handleImportarPlantilla) — se sanea igual que cualquier otro texto.
          row[sanitizarCeldaExcel(col.titleText)] = valorDeCelda(col, record[col.key]);
        });
        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];
      if (excelData.length > 0) {
        worksheet['!cols'] = Object.keys(excelData[0]).map((key) => ({
          wch: Math.max(key.length, ...excelData.map((row) => String(row[key] || '').length)) + 2,
        }));
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      message.success(`Exportados ${excelData.length} registros con ${orderedSelectedColumns.length} columnas`);
      onCancel();
    } catch (err) {
      message.error('Error al generar el Excel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Exportar Excel Personalizado"
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel} type="link">
          Cancelar
        </Button>,
        <Button key="confirm" onClick={handleExportar} type="link" loading={loading}>
          Exportar Excel
        </Button>,
      ]}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={16}>
          <AntSelect
            style={{ width: '100%' }}
            placeholder="Cargar una configuración guardada..."
            allowClear
            onChange={handleCargarConfiguracion}
          >
            {configuracionesGuardadas.map((config) => (
              <Option key={config.id} value={config.id}>
                <Row justify="space-between" align="middle">
                  {config.nombre}
                  <Popconfirm
                    title="¿Eliminar esta configuración guardada?"
                    onConfirm={(e) => {
                      e.stopPropagation();
                      handleEliminarConfiguracion(config.id);
                    }}
                    onCancel={(e) => e.stopPropagation()}
                  >
                    <DeleteOutlined onClick={(e) => e.stopPropagation()} style={{ color: '#ff4d4f' }} />
                  </Popconfirm>
                </Row>
              </Option>
            ))}
          </AntSelect>
        </Col>
        <Col span={8}>
          <Input.Group compact style={{ display: 'flex' }}>
            <Input
              placeholder="Nombre de la nueva configuración"
              value={nombreNuevaConfiguracion}
              onChange={(e) => setNombreNuevaConfiguracion(e.target.value)}
            />
            <Button icon={<SaveOutlined />} onClick={handleGuardarConfiguracion} />
          </Input.Group>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Tooltip title="Descarga la selección de columnas actual como un archivo, para compartirla con otro usuario">
            <Button icon={<DownloadOutlined />} onClick={handleDescargarPlantilla}>
              Descargar plantilla
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Upload accept="application/json" showUploadList={false} beforeUpload={handleImportarPlantilla}>
            <Tooltip title="Sube un archivo de plantilla que te haya compartido otro usuario">
              <Button icon={<UploadOutlined />}>
                Cargar plantilla
              </Button>
            </Tooltip>
          </Upload>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <div style={{
            marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
          >
            <strong>
              Columnas disponibles (
              {filteredAvailableColumns.length}
              )
            </strong>
            <Button size="small" onClick={handleSelectAll} type="link">
              Seleccionar todas
            </Button>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Buscar columnas disponibles..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>
          <div style={{
            maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', padding: 8, borderRadius: 4,
          }}
          >
            <List
              dataSource={filteredAvailableColumns}
              renderItem={(col) => (
                <List.Item style={{ cursor: 'pointer', padding: '8px' }} onClick={() => handleSelectColumn(col)} hoverable>
                  <Tooltip title={col.dataIndex}>
                    <strong>{col.titleText}</strong>
                    <br />
                    <small style={{ color: '#999' }}>{col.dataIndex}</small>
                  </Tooltip>
                </List.Item>
              )}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{
            marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
          >
            <strong>
              Columnas seleccionadas (
              {orderedSelectedColumns.length}
              )
            </strong>
            <Button size="small" danger onClick={handleDeselectAll} type="link">
              Eliminar todas
            </Button>
          </div>
          <div style={{
            maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', padding: 8, borderRadius: 4, backgroundColor: '#fafafa',
          }}
          >
            <List
              dataSource={orderedSelectedColumns}
              renderItem={(col, index) => (
                <List.Item
                  style={{
                    backgroundColor: 'white', marginBottom: 4, padding: '8px 12px', borderRadius: 4, border: '1px solid #e8e8e8',
                  }}
                  actions={[
                    <Button type="text" icon={<ArrowUpOutlined />} onClick={() => handleMoveUp(index)} disabled={index === 0} size="small" />,
                    <Button
                      type="text"
                      icon={<ArrowDownOutlined />}
                      onClick={() => handleMoveDown(index)}
                      disabled={index === orderedSelectedColumns.length - 1}
                      size="small"
                    />,
                    <Button type="text" danger onClick={() => handleRemoveColumn(col)} size="small">Eliminar</Button>,
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <Input
                      size="small"
                      value={col.titleText}
                      onChange={(e) => handleChangeTitulo(col, e.target.value)}
                      style={{ fontWeight: 'bold', marginBottom: 4 }}
                    />
                    <Row justify="space-between" align="middle">
                      <small style={{ color: '#999' }}>{col.dataIndex}</small>
                      <AntSelect size="small" value={col.formato} onChange={(v) => handleChangeFormato(col, v)} style={{ width: 100 }}>
                        <Option value="texto">Texto</Option>
                        <Option value="moneda">Moneda</Option>
                        <Option value="fecha">Fecha</Option>
                        <Option value="booleano">Booleano</Option>
                      </AntSelect>
                    </Row>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

ExportadorExcelPersonalizado.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.arrayOf(PropTypes.object),
  fileName: PropTypes.string,
  sheetName: PropTypes.string,
  reporteId: PropTypes.string,
  titulosPorColumna: PropTypes.objectOf(PropTypes.string),
  formatosPorColumna: PropTypes.objectOf(PropTypes.oneOf(['texto', 'moneda', 'fecha', 'booleano'])),
  columnasPorDefecto: PropTypes.arrayOf(PropTypes.string),
  agrupacion: PropTypes.shape({
    columna: PropTypes.string.isRequired,
    etiqueta: PropTypes.string,
    encabezado: PropTypes.arrayOf(PropTypes.string),
  }),
};

ExportadorExcelPersonalizado.defaultProps = {
  data: [],
  fileName: 'Reporte',
  sheetName: 'Reporte',
  reporteId: '',
  titulosPorColumna: {},
  formatosPorColumna: {},
  columnasPorDefecto: [],
  agrupacion: null,
};

export default ExportadorExcelPersonalizado;
